import { SectionRole } from "../../shared/types/roles";

import {
  classSessionSummarySelectors,
  classSessionUserSummarySelectors,
  createAppSelector,
  selectCurrentUserId,
} from "./base";
import { selectFilteredClassSessionsByDateRange } from "./class-session";

import type { ExtendedClassSessionSummaryType } from "api/api-data-types";
import { AttendanceStatus, ClassSessionCategory } from "components/server-types";
import {
  MIN_QUALIFYING_ATTENDANCE_PERCENTAGE,
  MIN_QUALIFYING_DURATION_MINUTES,
  getClassSessionDurationInMinutes,
} from "utils/metrics-utils";

export const selectStudentClassSessionUserSummaries = createAppSelector(
  [classSessionUserSummarySelectors.filterForCurrentSection],
  (summaries) => summaries.filter((s) => s.role === SectionRole.STUDENT),
);

export const selectExtendedClassSessionSummariesForDateRange = createAppSelector(
  [
    selectFilteredClassSessionsByDateRange,
    classSessionSummarySelectors.filterForCurrentSection,
    classSessionUserSummarySelectors.filterForCurrentSection,
    selectCurrentUserId,
  ],
  (sessions, summaries, userSummaries, currentUserId) =>
    sessions.map((session) => {
      const summary = summaries.find((s) => s.class_session_id === session.id) ?? {
        effective_ended_at: session.ended_at,
        hand_raise_count: 0,
        reaction_count: 0,
        chat_message_count: 0,
      };

      const sessionUserSummaries = userSummaries.filter((us) => us.class_session_id === session.id);
      const students: string[] = [];
      const attending_students: string[] = [];
      const late_students: string[] = [];

      let instructor_talk_time_seconds = 0;
      let student_talk_time_seconds = 0;
      let currentUserPresenceMinutes: number | null = null;
      let currentUserAttendanceStatus = AttendanceStatus.Absent;

      for (const userSummary of sessionUserSummaries) {
        const talkTimeSeconds = userSummary.talk_time_seconds ?? 0;
        const role = userSummary.role ?? "";
        const status = userSummary.attendance_status ?? AttendanceStatus.Absent;

        if (role === SectionRole.STUDENT) {
          students.push(userSummary.user_id);
          if (status !== AttendanceStatus.Absent) {
            attending_students.push(userSummary.user_id);
          }

          if (status === AttendanceStatus.Late) {
            late_students.push(userSummary.user_id);
          }

          student_talk_time_seconds += talkTimeSeconds;
        } else if (role === SectionRole.INSTRUCTOR) {
          instructor_talk_time_seconds += talkTimeSeconds;
        }

        if (currentUserId && userSummary.user_id === currentUserId) {
          currentUserPresenceMinutes = userSummary.presence_minutes ?? 0;
          currentUserAttendanceStatus = status;
        }
      }

      const attendance_percentage =
        students.length > 0 ? (attending_students.length / students.length) * 100 : 0;
      const lateness_percentage =
        students.length > 0 ? (late_students.length / students.length) * 100 : 0;

      return {
        ...summary,
        ...session, // after summary to override id
        class_session_id: session.id,
        started_at: session.started_at,
        students,
        attending_students,
        attendance_percentage,
        late_students,
        lateness_percentage,
        instructor_talk_time_seconds,
        student_talk_time_seconds,
        student_presence_minutes: currentUserPresenceMinutes ?? null,
        student_attendance_status: currentUserAttendanceStatus,
      } as ExtendedClassSessionSummaryType;
    }),
);

/**
 * Filter class sessions to only include qualifying sessions:
 * - >= 30 minutes duration
 * - >= 25% of class attending
 * - Not hidden (already filtered by selectFilteredClassSessionsByDateRange)
 */
const _selectQualifyingSessionsForDateRange = createAppSelector(
  [selectExtendedClassSessionSummariesForDateRange],
  (extendedSummaries) =>
    extendedSummaries.filter((session) => {
      const duration = getClassSessionDurationInMinutes(session);
      if (duration < MIN_QUALIFYING_DURATION_MINUTES) return false;
      return session.attendance_percentage / 100 >= MIN_QUALIFYING_ATTENDANCE_PERCENTAGE;
    }),
);

export const selectQualifyingSessionIdsForDateRange = createAppSelector(
  [_selectQualifyingSessionsForDateRange],
  (sessions) => new Set(sessions.map((s) => s.class_session_id)),
);

export const selectHasVirtualSessionsForQualifyingSessionsInDateRange = createAppSelector(
  [_selectQualifyingSessionsForDateRange],
  (sessions) => sessions.some((session) => session.category === ClassSessionCategory.VIRTUAL),
);

export const selectTotalQualifyingSessionsForDateRange = createAppSelector(
  [_selectQualifyingSessionsForDateRange],
  (sessions) => sessions.length,
);
